import { useState } from 'react';
import { Button } from '@ai-tutor/ui';
import { ZoomIn, ZoomOut, RotateCcw, Image as ImageIcon } from 'lucide-react';
import type { Source } from '@/data/fixtures';
import { useSourceFileUrl } from '@/hooks/lesson/useSources';
import {
  ViewerLoading,
  ViewerError,
} from '@/components/features/learning/sources/viewers/shell/ViewerStates';
import { ViewerShell } from '@/components/features/learning/sources/viewers/shell/ViewerShell';

interface ImageViewerProps {
  source: Source;
  // Kept for the legacy SourceViewer API shape - the viewer fetches its
  // own presigned URL now, so the prop is unused. Keep for compatibility.
  onDownload?: (source: Source) => void;
}

export function ImageViewer({ source }: ImageViewerProps) {
  const [zoom, setZoom] = useState(100);
  const { data, isLoading, error } = useSourceFileUrl(source.id);

  if (isLoading) return <ViewerLoading />;
  if (error || !data?.url)
    return (
      <ViewerError
        message="Couldn't load image"
        detail={(error as Error | undefined)?.message}
      />
    );

  const zoomControls = (
    <>
      <Button
        size='icon'
        variant='ghost'
        className='tap-target h-8 w-8 text-muted-foreground hover:text-foreground'
        onClick={() => setZoom(z => Math.max(25, z - 25))}
        title='Zoom out'
        aria-label='Zoom out'
      >
        <ZoomOut className='h-4 w-4' />
      </Button>
      <span className='min-w-12 text-center text-xs tabular-nums text-muted-foreground'>
        {zoom}%
      </span>
      <Button
        size='icon'
        variant='ghost'
        className='tap-target h-8 w-8 text-muted-foreground hover:text-foreground'
        onClick={() => setZoom(z => Math.min(400, z + 25))}
        title='Zoom in'
        aria-label='Zoom in'
      >
        <ZoomIn className='h-4 w-4' />
      </Button>
      <Button
        size='icon'
        variant='ghost'
        className='tap-target h-8 w-8 text-muted-foreground hover:text-foreground'
        onClick={() => setZoom(100)}
        disabled={zoom === 100}
        title='Reset zoom'
        aria-label='Reset zoom'
      >
        <RotateCcw className='h-4 w-4' />
      </Button>
    </>
  );

  return (
    <ViewerShell
      sourceId={source.id}
      fullscreen
      toolbarExtra={zoomControls}
      icon={ImageIcon}
      label='Image'
      tone='notes'
    >
      <div className='flex h-full items-center justify-center overflow-auto p-6'>
        <img
          src={data.url}
          alt={source.name}
          className='max-h-full max-w-full rounded-xl object-contain shadow-xl ring-1 ring-border'
          style={{
            transform: `scale(${zoom / 100})`,
            transition: 'transform 0.2s',
          }}
        />
      </div>
    </ViewerShell>
  );
}
